import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { createDarkTheme, createLightTheme } from './themes';

const ThemeContext = createContext({
  mode: 'light',
  toggleTheme: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

// Light is the house style -- it is the one that matches the printed AiViz
// documents -- so an unconfigured user gets light, whatever the OS prefers.
const getInitialMode = () => {
  const stored = localStorage.getItem('pms-theme-mode');
  if (stored === 'light' || stored === 'dark') return stored;
  return 'light';
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('pms-theme-mode', next);
      return next;
    });
  };

  const theme = useMemo(
    () => (mode === 'dark' ? createDarkTheme() : createLightTheme()),
    [mode],
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const contextValue = useMemo(() => ({ mode, toggleTheme }), [mode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
